import { Fragment, useState, type ReactNode } from 'react';
import {
  type ColumnDef,
  type RowData,
  columnSizingFeature,
  columnVisibilityFeature,
  flexRender,
  tableFeatures,
  useTable,
} from '@tanstack/react-table';
import { ChevronRight } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/atoms/table';
import { ErrorState } from '@/components/molecules/ErrorState';
import { Loading } from '@/components/molecules/Loading';
import { TablePagination } from '@/components/molecules/TablePagination';
import { useIsMobile } from '@/hooks/ui/useMobile';
import { cn } from '@/libs/utils/cn';

const features = tableFeatures({ columnSizingFeature, columnVisibilityFeature });

function primaryColumnLabel(
  row: { getVisibleCells: () => { column: { columnDef: { header?: unknown } } }[] } | undefined,
): string | null {
  if (!row) return null;
  for (const cell of row.getVisibleCells()) {
    const header = cell.column.columnDef.header;
    if (typeof header === 'string' && header !== '') return header;
  }
  return null;
}

export type DataTableColumnDef<TData extends RowData> = ColumnDef<typeof features, TData>;

interface PaginationData {
  currentPage: number;
  totalPages: number;
  onPageChange: (newPage: number) => void;
}

interface DataTableProps<TData extends RowData> {
  columns: DataTableColumnDef<TData>[];
  data: TData[];
  onRowClick?: (row: TData) => void;
  isRowClickable?: (row: TData) => boolean;
  /**
   * Extra HTML attributes applied to each row element. Row className, interaction
   * handlers (onClick, onKeyDown), role, and tabIndex are controlled by DataTable
   * itself — use `rowClassName` for per-row styling instead.
   */
  rowAttributes?: (
    row: TData,
  ) => Omit<
    React.HTMLAttributes<HTMLTableRowElement>,
    'className' | 'onClick' | 'onKeyDown' | 'role' | 'tabIndex'
  >;
  pagination?: PaginationData;

  // Loading state
  isLoading?: boolean;
  loadingContent?: React.ReactNode;

  // Error state
  isError?: boolean;
  errorContent?: React.ReactNode;
  errorMessage?: string;
  errorDescription?: string;
  onRetry?: () => void;

  // Empty state
  emptyContent?: React.ReactNode;
  emptyMessage?: string;

  // Per-row class hook (e.g. tint rows that have validation issues).
  // Called on every render for every visible row — keep it pure and inexpensive.
  rowClassName?: (row: TData) => string | undefined;

  // Table styling
  className?: string;
}

export function DataTable<TData extends RowData>({
  columns,
  data,
  onRowClick,
  isRowClickable,
  rowAttributes,
  pagination,
  isLoading = false,
  loadingContent,
  isError = false,
  errorContent,
  errorMessage = 'Failed to load data',
  errorDescription = 'An error occurred while fetching the data. Please try again.',
  onRetry,
  emptyContent,
  emptyMessage = 'No results.',
  rowClassName,
  className,
}: DataTableProps<TData>) {
  const compact = useIsMobile();
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  const table = useTable({
    features,
    data,
    columns,
  });

  const isClickable = (row: TData) =>
    Boolean(onRowClick && (!isRowClickable || isRowClickable(row)));

  // Render loading state
  if (isLoading) {
    return (
      <div className={className}>
        {loadingContent ?? (
          <div className="w-full h-full">
            <Loading className="min-h-full" />
          </div>
        )}
      </div>
    );
  }

  // Default error content with retry button
  const defaultErrorContent = (
    <ErrorState
      title={errorMessage}
      description={errorDescription}
      actions={
        onRetry && (
          <Button variant="outline" size="sm" onClick={onRetry}>
            Try again
          </Button>
        )
      }
    />
  );

  const bodyRows = table.getRowModel().rows;

  if (compact) {
    return (
      <div className={className}>
        {isError ? (
          (errorContent ?? defaultErrorContent)
        ) : bodyRows.length === 0 ? (
          (emptyContent ?? (
            <p className="py-8 text-center text-sm text-muted-foreground">{emptyMessage}</p>
          ))
        ) : (
          <div className="overflow-x-hidden">
            <Table className="table-fixed">
              <TableHeader>
                <TableRow className="border-primary-100">
                  <TableHead className="bg-list-header px-4 py-3 text-xs font-medium uppercase tracking-wide text-primary-600">
                    {primaryColumnLabel(bodyRows[0])}
                  </TableHead>
                  {bodyRows[0]
                    ?.getVisibleCells()
                    .some((cell) => cell.column.columnDef.header === '') && (
                    <TableHead className="bg-list-header w-24 px-4 py-3" />
                  )}
                  <TableHead className="bg-list-header w-12 px-2 py-3" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {bodyRows.map((row) => {
                  const clickable = isClickable(row.original);
                  const labeled: { key: string; label: string; node: ReactNode }[] = [];
                  const actions: { key: string; node: ReactNode }[] = [];
                  for (const cell of row.getVisibleCells()) {
                    const header = cell.column.columnDef.header;
                    const label = typeof header === 'string' ? header : '';
                    const node = flexRender(cell.column.columnDef.cell, cell.getContext());
                    if (label === '') actions.push({ key: cell.id, node });
                    else labeled.push({ key: cell.id, label, node });
                  }
                  const [primary, ...details] = labeled;
                  const isOpen = expanded.has(row.id);
                  const colSpan = 1 + (actions.length > 0 ? 1 : 0) + (details.length > 0 ? 1 : 0);
                  return (
                    <Fragment key={row.id}>
                      <TableRow
                        {...rowAttributes?.(row.original)}
                        className={cn(
                          'border-primary-100',
                          clickable && 'cursor-pointer',
                          !clickable && onRowClick && isRowClickable && 'cursor-not-allowed',
                          rowClassName?.(row.original),
                        )}
                      >
                        <TableCell
                          className="min-w-0 overflow-hidden px-4 py-3.75 whitespace-normal break-words"
                          role={clickable ? 'button' : undefined}
                          tabIndex={clickable ? 0 : undefined}
                          onClick={() => {
                            if (clickable) onRowClick!(row.original);
                          }}
                          onKeyDown={(event) => {
                            if (!clickable) return;
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              onRowClick!(row.original);
                            }
                          }}
                        >
                          {primary?.node}
                        </TableCell>
                        {actions.length > 0 && (
                          <TableCell className="w-24 px-2 py-3.75">
                            <div className="flex items-center justify-end gap-2">
                              {actions.map((action) => (
                                <div key={action.key}>{action.node}</div>
                              ))}
                            </div>
                          </TableCell>
                        )}
                        {details.length > 0 && (
                          <TableCell className="w-12 px-2 py-3.75">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              aria-expanded={isOpen}
                              aria-label={isOpen ? 'Hide details' : 'Show details'}
                              onClick={() => {
                                setExpanded((current) => {
                                  const next = new Set(current);
                                  if (next.has(row.id)) next.delete(row.id);
                                  else next.add(row.id);
                                  return next;
                                });
                              }}
                            >
                              <ChevronRight
                                className="size-4 transition-transform"
                                style={{ transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}
                              />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                      {isOpen && details.length > 0 && (
                        <TableRow className="border-primary-100 hover:bg-transparent">
                          <TableCell colSpan={colSpan} className="p-0 whitespace-normal">
                            <div className="divide-y divide-primary-100">
                              {details.map((field) => (
                                <div
                                  key={field.key}
                                  className="grid grid-cols-[7.5rem_minmax(0,1fr)]"
                                >
                                  <div className="bg-list-header px-4 py-3 text-xs font-medium uppercase tracking-wide text-primary-600">
                                    {field.label}
                                  </div>
                                  <div className="min-w-0 px-4 py-3.75 text-sm break-words">
                                    {field.node}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
        {pagination && !isError && (
          <div className="mt-6">
            <TablePagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={pagination.onPageChange}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="overflow-x-hidden">
        <Table className="table-fixed">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="border-primary-100">
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      className={
                        'bg-list-header text-primary-600 text-xs font-medium uppercase tracking-wide whitespace-normal py-3 px-4'
                      }
                      style={{ width: 'auto' }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isError ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="py-8">
                  {errorContent ?? defaultErrorContent}
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => {
                const clickable = isClickable(row.original);
                return (
                  <TableRow
                    key={row.id}
                    {...rowAttributes?.(row.original)}
                    className={cn(
                      'border-primary-100',
                      clickable && 'cursor-pointer',
                      !clickable && onRowClick && isRowClickable && 'cursor-not-allowed',
                      rowClassName?.(row.original),
                    )}
                    role={clickable ? 'button' : undefined}
                    tabIndex={clickable ? 0 : -1}
                    onClick={() => {
                      if (clickable) onRowClick!(row.original);
                    }}
                    onKeyDown={(event) => {
                      if (!clickable) return;
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onRowClick!(row.original);
                      }
                    }}
                  >
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <TableCell
                          key={cell.id}
                          className="min-w-0 overflow-hidden px-4 py-3.75 whitespace-normal break-words"
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center py-3 pl-4 pr-4">
                  {emptyContent ?? emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {pagination && !isError && (
          <div className="mt-6">
            <TablePagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={pagination.onPageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
}
